import React, { useState } from "react";
import {
    ChevronRight,
    ChevronDown,
    FileText,
    FolderOpen,
    Plus,
    Trash2,
    Pencil,
    Check,
    X,
} from "lucide-react";
import { ICON_MAP } from "./WikiIcons";

export default function WikiSidebar({
    tree,
    selectedId,
    onSelect,
    onCreate,
    onDelete,
    onRename,
    depth = 0,
}) {
    return (
        <nav className="flex-1 overflow-y-auto py-2">
            {tree.length === 0 ? (
                <div className="px-4 py-8 text-center font-mono text-xs opacity-50">
                    No pages yet.
                    {onCreate && (
                        <button
                            onClick={() => onCreate(null)}
                            className="block mx-auto mt-2 underline hover:no-underline"
                        >
                            Create the first page
                        </button>
                    )}
                </div>
            ) : (
                tree.map((node) => (
                    <TreeNode
                        key={node.id}
                        node={node}
                        selectedId={selectedId}
                        onSelect={onSelect}
                        onCreate={onCreate}
                        onDelete={onDelete}
                        onRename={onRename}
                        depth={depth}
                    />
                ))
            )}
        </nav>
    );
}

function TreeNode({
    node,
    selectedId,
    onSelect,
    onCreate,
    onDelete,
    onRename,
    depth,
}) {
    const [expanded, setExpanded] = useState(depth < 2);
    const [renaming, setRenaming] = useState(false);
    const [renameValue, setRenameValue] = useState(node.title);
    const [hovering, setHovering] = useState(false);

    const hasChildren = node.children && node.children.length > 0;
    const isSelected = selectedId === node.id;
    const Icon = ICON_MAP[node.icon] || FileText;

    const handleRenameSubmit = () => {
        const trimmed = renameValue.trim();
        if (trimmed && trimmed !== node.title && onRename) {
            onRename(node.id, trimmed);
        }
        setRenaming(false);
    };

    return (
        <div>
            <div
                className={`group flex items-center gap-1 px-2 py-1 cursor-pointer text-sm font-mono
                    ${isSelected ? "bg-black text-white" : "hover:bg-gray-100"}
                    ${depth > 0 ? "pl-" + (8 + depth * 12) : "pl-2"}`}
                style={{ paddingLeft: `${8 + depth * 14}px` }}
                onMouseEnter={() => setHovering(true)}
                onMouseLeave={() => setHovering(false)}
                onClick={() => {
                    if (!renaming) onSelect(node.slug);
                }}
            >
                {/* Expand/collapse */}
                <button
                    className="p-0 w-4 shrink-0"
                    onClick={(e) => {
                        e.stopPropagation();
                        setExpanded(!expanded);
                    }}
                >
                    {hasChildren ? (
                        expanded ? (
                            <ChevronDown size={12} strokeWidth={3} />
                        ) : (
                            <ChevronRight size={12} strokeWidth={3} />
                        )
                    ) : (
                        <span className="w-3" />
                    )}
                </button>

                {/* Icon */}
                <Icon size={14} strokeWidth={2.5} className="shrink-0 opacity-70" />

                {/* Title or rename input */}
                {renaming ? (
                    <div className="flex items-center gap-1 flex-1 min-w-0">
                        <input
                            className="flex-1 min-w-0 text-sm font-mono bg-white text-black px-1 border border-black outline-none"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleRenameSubmit();
                                if (e.key === "Escape") setRenaming(false);
                            }}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                        />
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRenameSubmit();
                            }}
                            className="p-0.5"
                        >
                            <Check size={12} strokeWidth={3} />
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setRenaming(false);
                                setRenameValue(node.title);
                            }}
                            className="p-0.5"
                        >
                            <X size={12} strokeWidth={3} />
                        </button>
                    </div>
                ) : (
                    <span className="truncate flex-1">{node.title}</span>
                )}

                {/* Action buttons on hover */}
                {hovering && !renaming && (onCreate || onDelete || onRename) && (
                    <div className="flex items-center gap-0.5 shrink-0">
                        {onRename && (
                            <button
                                className="p-0.5 opacity-50 hover:opacity-100"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setRenameValue(node.title);
                                    setRenaming(true);
                                }}
                                title="Rename"
                            >
                                <Pencil size={11} strokeWidth={3} />
                            </button>
                        )}
                        {onCreate && (
                            <button
                                className="p-0.5 opacity-50 hover:opacity-100"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onCreate(node.id);
                                }}
                                title="Add sub-page"
                            >
                                <Plus size={11} strokeWidth={3} />
                            </button>
                        )}
                        {onDelete && (
                            <button
                                className="p-0.5 opacity-50 hover:opacity-100 text-[#E8241A]"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (
                                        window.confirm(
                                            `Delete "${node.title}"? Its children will be moved up.`,
                                        )
                                    ) {
                                        onDelete(node.id);
                                    }
                                }}
                                title="Delete"
                            >
                                <Trash2 size={11} strokeWidth={3} />
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Children — render directly to avoid cross-component recursion */}
            {expanded && hasChildren &&
                node.children.map((child) => (
                    <TreeNode
                        key={child.id}
                        node={child}
                        selectedId={selectedId}
                        onSelect={onSelect}
                        onCreate={onCreate}
                        onDelete={onDelete}
                        onRename={onRename}
                        depth={depth + 1}
                    />
                ))}
        </div>
    );
}
